import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { useSendToAgenciesFormContext } from 'modals/ShiftModal/src/views/SendToAgenciesView/SendToAgencies.form.context';
import { useGQLQuery } from 'hooks';
import { UseGQLQuery, UseGQLQueryConfig } from 'hooks/useGQLQuery/useGQLQuery.types';
import { nonPaginatedApolloClient } from 'providers/ApolloClients/NonPaginatedApolloClient';
import { QUERY_sendToSelectedAgenciesFetchMarketplaceCommunitiesByOrderIdNonPaginated } from 'queries';
import { FC, useContext, useEffect, useMemo } from 'react';
import { createGQLDataContext } from 'utils/createDataContext';

export interface IFetchMarketplaceByCommunityIdsData {
  selectedMarketplaceCommunities: string[];
  availableJaneCount: number;
  hourlyRate: {
    min: number;
    max: number;
  };
}

interface IMarketplaceNode {
  name: string;
  availableJanes: {
    count: number;
  };
  communitiesPositions: {
    chargeRate: string;
  }[];
}

interface IFetchMarketplaceCommunitiesResponse {
  fetchMarketplaceCommunities: {
    nodes: IMarketplaceNode[];
  };
}

const fetchSelectedMarketplaceQueryConfig = ({
  orderId,
  selectedMarketplaceCommunities,
}: {
  orderId: string;
  selectedMarketplaceCommunities: number[];
}): UseGQLQueryConfig<
  IFetchMarketplaceCommunitiesResponse,
  IFetchMarketplaceByCommunityIdsData
> => ({
  key: 'SEND_TO_AGENCIES_SUMMARY_VIEW_FETCH_MARKETPLACE_QUERY',
  client: nonPaginatedApolloClient(),
  query: QUERY_sendToSelectedAgenciesFetchMarketplaceCommunitiesByOrderIdNonPaginated,
  responseTransformer: (res): IFetchMarketplaceByCommunityIdsData => {
    const nodes = res?.fetchMarketplaceCommunities?.nodes || [];
    const filteredRates =
      nodes
        .map((marketplaceCommunity) => marketplaceCommunity.communitiesPositions[0]?.chargeRate)
        .filter((rate) => !!rate)
        .map((rate) => Number.parseFloat(String(rate).replace('$', ''))) || [];

    return {
      selectedMarketplaceCommunities: nodes.map(
        (marketplaceCommunity) => marketplaceCommunity.name,
      ),
      availableJaneCount: nodes
        .map((marketplaceCommunity) => marketplaceCommunity.availableJanes.count)
        .reduce((accum: number, curr: number) => curr + accum, 0),
      hourlyRate: {
        min: filteredRates.length > 0 ? Math.min(...filteredRates) : 0,
        max: filteredRates.length > 0 ? Math.max(...filteredRates) : 0,
      },
    };
  },
  variables: {
    orderId,
    selectedMarketplaceCommunities,
  },
});

export const FetchMarketplaceByCommunityIdsDataContext =
  createGQLDataContext<IFetchMarketplaceByCommunityIdsData>(
    {} as IFetchMarketplaceByCommunityIdsData,
  );

export const FetchMarketplaceByCommunityIdsDataProvider: FC = ({ children }) => {
  const { data } = useShiftModalDataContext();
  const orderId = data!.order.relayId;
  const {
    values: { selectedMarketplaceCommunities },
  } = useSendToAgenciesFormContext();

  const config = useMemo(
    () =>
      fetchSelectedMarketplaceQueryConfig({
        orderId,
        selectedMarketplaceCommunities,
      }),
    [orderId, selectedMarketplaceCommunities],
  );

  const selectedMarketplaceInfo = useGQLQuery(config);

  useEffect(() => {
    if (selectedMarketplaceCommunities.length > 0) {
      selectedMarketplaceInfo.fetch();
    }
  }, [selectedMarketplaceCommunities]);

  const contextValue = useMemo(() => selectedMarketplaceInfo, [selectedMarketplaceInfo]);

  return (
    <FetchMarketplaceByCommunityIdsDataContext.Provider value={contextValue}>
      {children}
    </FetchMarketplaceByCommunityIdsDataContext.Provider>
  );
};

export const useFetchMarketplaceByCommunityIdsDataContext =
  (): UseGQLQuery<IFetchMarketplaceByCommunityIdsData> =>
    useContext(FetchMarketplaceByCommunityIdsDataContext);
